import { Badge, Button, Grid, Group, Modal, Paper, Stack, Table, Text, TextInput, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconDownload, IconPlus } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, authFetch, unwrap } from "../api/client";
import { useCan } from "../auth/user";
import { useT } from "../i18n/script";

export default function FarmPage() {
  const { t } = useT();
  const can = useCan();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [sel, setSel] = useState<number | null>(null);
  const [form, setForm] = useState<{ name: string; bpg: string; holder_name: string; municipality: string } | null>(null);

  const holdings = useQuery({ queryKey: ["holdings", q],
    queryFn: async () => unwrap(await api.GET("/api/v1/farm/holdings", { params: { query: { q: q || undefined } } })) });
  const parcels = useQuery({ queryKey: ["farm-parcels", sel], enabled: sel != null,
    queryFn: async () => unwrap(await api.GET("/api/v1/farm/parcels", { params: { query: { holding_id: sel! } } })) });
  const stats = useQuery({ queryKey: ["holding-stats", sel], enabled: sel != null,
    queryFn: async () => unwrap(await api.GET("/api/v1/farm/holdings/{holding_id}/stats", { params: { path: { holding_id: sel! } } })) as
      { by_crop: { crop: string; parcels: number; area_ha: number; ndvi_30d: number | null }[]; total_area_ha: number } });

  async function create() {
    if (!form) return;
    try {
      unwrap(await api.POST("/api/v1/farm/holdings", { body: { ...form, contact: {}, attributes: {} } }));
      setForm(null);
      qc.invalidateQueries({ queryKey: ["holdings"] });
    } catch (e) { notifications.show({ color: "red", message: String(e) }); }
  }

  async function exportGeojson() {
    const r = await authFetch(`/api/v1/farm/parcels.geojson${sel ? `?holding_id=${sel}` : ""}`);
    const url = URL.createObjectURL(await r.blob());
    const a = document.createElement("a");
    a.href = url; a.download = "parcele.geojson"; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Stack>
      <Group justify="space-between">
        <div>
          <Title order={3}>{t("Gazdinstva i parcele")}</Title>
          <Text size="sm" c="dimmed">{t("Registar poljoprivrednih gazdinstava (BPG) i obradivih površina")}</Text>
        </div>
        <Group>
          <Button variant="default" leftSection={<IconDownload size={16} />} onClick={exportGeojson}>GeoJSON</Button>
          {can("holdings:write") && <Button leftSection={<IconPlus size={16} />}
            onClick={() => setForm({ name: "", bpg: "", holder_name: "", municipality: "" })}>{t("Novo gazdinstvo")}</Button>}
        </Group>
      </Group>
      <Grid>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Paper withBorder p="sm">
            <TextInput placeholder={t("Pretraga (naziv, BPG, nosilac)")} value={q} onChange={(e) => setQ(e.currentTarget.value)} mb="sm" />
            <Table highlightOnHover fz="sm">
              <Table.Thead><Table.Tr><Table.Th>BPG</Table.Th><Table.Th>{t("Naziv")}</Table.Th><Table.Th>{t("Parcela")}</Table.Th><Table.Th>ha</Table.Th></Table.Tr></Table.Thead>
              <Table.Tbody>
                {(holdings.data ?? []).map((h) => (
                  <Table.Tr key={h.id} onClick={() => setSel(h.id)} style={{ cursor: "pointer" }}
                    bg={sel === h.id ? "var(--mantine-color-agri-light)" : undefined}>
                    <Table.Td ff="monospace">{h.bpg}</Table.Td><Table.Td>{h.name}</Table.Td>
                    <Table.Td>{h.parcel_count}</Table.Td><Table.Td>{h.total_area_ha?.toFixed(2)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {!holdings.data?.length && <Text c="dimmed" size="sm" ta="center" py="md">{t("Nema gazdinstava")}</Text>}
          </Paper>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Paper withBorder p="sm" mih={200}>
            {sel == null ? <Text c="dimmed">{t("Izaberi gazdinstvo")}</Text> : (
              <Stack>
                <Group gap="xs">{(stats.data?.by_crop ?? []).map((c) => (
                  <Badge key={c.crop} variant="light" size="lg">{c.crop}: {c.area_ha} ha</Badge>))}</Group>
                <Table fz="sm">
                  <Table.Thead><Table.Tr><Table.Th>ID</Table.Th><Table.Th>{t("Naziv")}</Table.Th><Table.Th>{t("Kultura")}</Table.Th>
                    <Table.Th>m²</Table.Th><Table.Th>{t("Katastarske parcele")}</Table.Th><Table.Th>v</Table.Th></Table.Tr></Table.Thead>
                  <Table.Tbody>
                    {(parcels.data ?? []).map((p) => (
                      <Table.Tr key={p.id}><Table.Td>{p.id}</Table.Td><Table.Td>{p.name}</Table.Td><Table.Td>{p.crop_code}</Table.Td>
                        <Table.Td>{p.area_m2.toLocaleString("sr")}</Table.Td><Table.Td>{p.cadastral_parcel_ids.length}</Table.Td>
                        <Table.Td>{p.version}</Table.Td></Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
                <Text size="xs" c="dimmed">{t("Parcele se crtaju, dijele i spajaju na mapi (API: /farm/parcels, /split, /merge).")}</Text>
              </Stack>
            )}
          </Paper>
        </Grid.Col>
      </Grid>
      <Modal opened={!!form} onClose={() => setForm(null)} title={t("Novo gazdinstvo")}>
        {form && (
          <Stack>
            <TextInput label="BPG" value={form.bpg} onChange={(e) => setForm({ ...form, bpg: e.currentTarget.value })} />
            <TextInput label={t("Naziv")} required value={form.name} onChange={(e) => setForm({ ...form, name: e.currentTarget.value })} />
            <TextInput label={t("Nosilac")} value={form.holder_name} onChange={(e) => setForm({ ...form, holder_name: e.currentTarget.value })} />
            <TextInput label={t("Opština")} value={form.municipality} onChange={(e) => setForm({ ...form, municipality: e.currentTarget.value })} />
            <Button onClick={create}>{t("Sačuvaj")}</Button>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
